import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  createDVIRTemplate,
  toggleShowAddTemplateModal,
} from "../../dvirSlice";
import "./AddTemplateModal.scss";

export function AddTemplateModal() {
  const dispatch = useAppDispatch();
  const showModal = useAppSelector((state) => state.dvir.showAddTemplateModal);
  const createAPI = useAppSelector((state) => state.dvir.createDVIRTemplate);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    templateType: "pre_trip" as "pre_trip" | "post_trip" | "end_of_week",
    isActive: true,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      templateType: "pre_trip",
      isActive: true,
    });
    setErrors({});
  };

  React.useEffect(() => {
    if (!showModal) {
      resetForm();
    }
  }, [showModal]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = "Template name is required";
    } else if (formData.name.trim().length < 3) {
      newErrors.name = "Template name must be at least 3 characters";
    } else if (formData.name.trim().length > 100) {
      newErrors.name = "Template name must be less than 100 characters";
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = "Description must be less than 500 characters";
    }

    if (!formData.templateType) {
      newErrors.templateType = "Template type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    dispatch(
      createDVIRTemplate({
        name: formData.name.trim(),
        description: formData.description.trim(),
        templateType: formData.templateType,
        isActive: formData.isActive,
      })
    );
  };

  const handleClose = () => {
    resetForm();
    dispatch(toggleShowAddTemplateModal());
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleSubmit();
    } else if (e.key === "Escape") {
      handleClose();
    }
  };

  if (!showModal) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="add-template-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="modal-header">
          <h3>Add New Template</h3>
          <button className="close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="modal-form">
          <div className="form-group">
            <label htmlFor="templateName">Template Name *</label>
            <input
              id="templateName"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Enter template name (e.g., 'Pre-Trip Inspection')"
              maxLength={100}
              className={errors.name ? "error" : ""}
            />
            {errors.name && (
              <span className="error-message">{errors.name}</span>
            )}
            <div className="character-count">
              {formData.name.length}/100 characters
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="templateType">Template Type *</label>
            <select
              id="templateType"
              value={formData.templateType}
              onChange={(e) =>
                handleInputChange("templateType", e.target.value)
              }
              className={errors.templateType ? "error" : ""}
            >
              <option value="pre_trip">Pre-Trip</option>
              <option value="post_trip">Post-Trip</option>
              <option value="end_of_week">End of Week</option>
            </select>
            {errors.templateType && (
              <span className="error-message">{errors.templateType}</span>
            )}
            <div className="help-text">
              {formData.templateType === "pre_trip" &&
                "Used for inspections before starting a trip"}
              {formData.templateType === "post_trip" &&
                "Used for inspections after completing a trip"}
              {formData.templateType === "end_of_week" &&
                "Used for weekly vehicle inspections"}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="templateDescription">Description</label>
            <textarea
              id="templateDescription"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Enter template description (optional)"
              rows={3}
              maxLength={500}
              className={errors.description ? "error" : ""}
            />
            {errors.description && (
              <span className="error-message">{errors.description}</span>
            )}
            <div className="character-count">
              {formData.description.length}/500 characters
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) =>
                  handleInputChange("isActive", e.target.checked)
                }
              />
              <span>Set as active template</span>
            </label>
            <div className="help-text">
              Active templates are available for use in DVIRs
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={createAPI.loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={createAPI.loading}
          >
            {createAPI.loading ? "Creating..." : "Create Template"}
          </button>
        </div>
      </div>
    </div>
  );
}
